import type { Pence } from './money.js';

/*
 * Placeholder menu, taken from Prototype D. Saif supplies the final menu and
 * prices (see TODO-SAIF.md). In production the menu lives in the database and
 * is edited in the admin; this seed is used for local dev, tests and the first
 * staging deploy. The API builds the same `Menu` shape from D1.
 */

export const FLAVOURS = ['Angara', 'Classic', 'Lassuni', 'Hariyali', 'Malai'] as const;
export type Flavour = (typeof FLAVOURS)[number];

export const PROTEINS = ['chicken', 'paneer'] as const;
export type Protein = (typeof PROTEINS)[number];

export const SAUCES = ['Mint', 'Garlic', 'Chilli'] as const;
export type Sauce = (typeof SAUCES)[number];

export const TOPPINGS = ['Iceberg lettuce', 'Tomato', 'Onion', 'Cucumber', 'Coriander', 'Lemon'] as const;
export type Topping = (typeof TOPPINGS)[number];

/** Display facts per flavour. Colours are the Docket POS flavour tokens (design/docket-pos/tokens.json), shared with the till. */
export interface FlavourInfo {
  name: Flavour;
  colour: string;
  /** 0–3 chillies */
  heat: number;
  taste: string;
  /** Short nickname used in bowl names ("The Fire & Cream Bowl"). */
  nick: string;
  note: string;
}
export const FLAVOUR_INFO: Record<Flavour, FlavourInfo> = {
  Angara: { name: 'Angara', colour: '#d42a14', heat: 3, taste: 'Hot', nick: 'Fire', note: 'Hot & fiery' },
  Classic: { name: 'Classic', colour: '#ff8a24', heat: 2, taste: 'Medium', nick: 'Classic', note: 'The OG' },
  Lassuni: { name: 'Lassuni', colour: '#6a3ec2', heat: 1, taste: 'Garlicky', nick: 'Garlic', note: 'Garlicky' },
  Hariyali: { name: 'Hariyali', colour: '#43b85f', heat: 0, taste: 'Herby', nick: 'Herb', note: 'Herby & zesty' },
  Malai: { name: 'Malai', colour: '#f3e2bd', heat: 0, taste: 'Creamy', nick: 'Cream', note: 'Silky cream' },
};

/** "Make it a meal": a side or dessert plus a drink. Placeholders. */
export const MEAL_SIDES = ['Chips', 'Tikki Tots', 'Gulab Jamun', 'Rasgulla'] as const;
export type MealSide = (typeof MEAL_SIDES)[number];
export const MEAL_SIDE_ITEM: Record<MealSide, string> = { Chips: 'chipsS', 'Tikki Tots': 'totsS', 'Gulab Jamun': 'gulab', Rasgulla: 'rasg' };
export const DRINKS = ['Coke', 'Coke Zero', 'Water'] as const;
export type Drink = (typeof DRINKS)[number];

export type Category = 'bowls' | 'just' | 'sides' | 'desserts' | 'drinks' | 'secret';

export const MENU_TABS: ReadonlyArray<readonly [Category, string]> = [
  ['bowls', 'Bowls & wraps'],
  ['just', 'Just Tikkas'],
  ['sides', 'Sides'],
  ['desserts', 'Desserts'],
];

export interface MenuSize {
  name: string;
  pricePence: Pence;
  /** For tikka items: how many flavour portions this size holds. */
  maxPortions?: number;
  /** Placeholder calories for this size (prototype sample figures). */
  kcal?: number;
}

export interface Upgrade {
  id: string;
  name: string;
  pricePence: Pence;
  note?: string;
  kcal?: number;
}

export interface MenuItem {
  id: string;
  name: string;
  category: Category;
  /** Customer picks flavour portions (bowls, wraps, Just Tikkas). */
  hasTikkas: boolean;
  hasToppings: boolean;
  mealAvailable: boolean;
  sizes: MenuSize[];
  upgradeIds: string[];
  soldOut?: boolean;
  /** Display (all placeholders until Saif supplies copy and photos). */
  description?: string;
  longDescription?: string;
  /** Image key served from the app's /public folder (placeholder-<image>.webp). */
  image?: string;
  /** A real photo uploaded in the admin (served by the API from R2). Wins over `image` when present. */
  photoUrl?: string;
  /** Sold out until this time (ISO), or for the rest of the day when soldOut is set without it. */
  soldOutUntil?: string;
  glutenFree?: boolean;
  /** What a built item is called: Bowl, Wrap, Box. */
  noun?: string;
  allergens?: string[];
  popular?: boolean;
  sortOrder?: number;
}

export interface Menu {
  items: Record<string, MenuItem>;
  upgrades: Record<string, Upgrade>;
  /** Price of "make it a meal" (a side plus a drink). */
  mealPricePence: Pence;
  /** Members' price for the dessert add-on in the builder (prototype: Gulab Jamun for £1). */
  membersDessertPricePence: Pence;
  soldOutFlavours: Flavour[];
  /** A flavour sold out in one protein only (Malai paneer, say). The whole flavour sold out is in soldOutFlavours. */
  soldOutPortions?: { flavour: Flavour; protein: Protein }[];
}

const tikkaSizes = (regular: Pence, large: Pence, kcal: [number, number]): MenuSize[] => [
  { name: 'Regular', pricePence: regular, maxPortions: 2, kcal: kcal[0] },
  { name: 'Large', pricePence: large, maxPortions: 3, kcal: kcal[1] },
];

export const SEED_UPGRADES: Record<string, Upgrade> = {
  tots: { id: 'tots', name: 'Tikki Tots', pricePence: 150, note: 'Crisp potato tots', kcal: 180 },
  cream: { id: 'cream', name: 'Cream & Butter', pricePence: 200, note: 'With chaat masala', kcal: 160 },
  ricein: { id: 'ricein', name: 'Rice inside', pricePence: 100, note: 'Cumin basmati', kcal: 120 },
  chipsin: { id: 'chipsin', name: 'Chips inside', pricePence: 100, note: 'Golden chips', kcal: 150 },
};

const MILK = ['Milk'];

export const SEED_ITEMS: Record<string, MenuItem> = {
  rice: {
    id: 'rice', name: 'Rice Bowl', category: 'bowls', hasTikkas: true, hasToppings: true, mealAvailable: true, sizes: tikkaSizes(995, 1295, [640, 820]), upgradeIds: ['tots', 'cream'],
    description: 'Cumin basmati, fresh toppings, house sauces', longDescription: 'Cumin basmati rice, fresh toppings and house sauces with your tikkas.', image: 'rice', glutenFree: true, noun: 'Bowl', allergens: MILK, popular: true, sortOrder: 10,
  },
  salad: {
    id: 'salad', name: 'Salad Bowl', category: 'bowls', hasTikkas: true, hasToppings: true, mealAvailable: true, sizes: tikkaSizes(995, 1295, [420, 560]), upgradeIds: ['tots', 'cream'],
    description: 'Crisp salad, fresh toppings, house sauces', longDescription: 'Crisp salad, fresh toppings and house sauces with your tikkas.', image: 'salad', glutenFree: true, noun: 'Bowl', allergens: MILK, sortOrder: 20,
  },
  tchips: {
    id: 'tchips', name: 'Tikkas & Chips', category: 'bowls', hasTikkas: true, hasToppings: false, mealAvailable: true, sizes: tikkaSizes(995, 1295, [780, 980]), upgradeIds: ['cream'],
    description: 'Golden chips with your tikkas and sauces', longDescription: 'Golden chips topped with your tikkas and house sauces.', image: 'chips', glutenFree: true, noun: 'Box', allergens: MILK, sortOrder: 30,
  },
  // CLAUDE.md says the wrap is £8.95. The Large wrap price is a placeholder (+£3, like the bowls).
  wrap: {
    id: 'wrap', name: 'Tikka Wrap', category: 'bowls', hasTikkas: true, hasToppings: true, mealAvailable: true, sizes: tikkaSizes(895, 1195, [690, 870]), upgradeIds: ['ricein', 'chipsin', 'cream'],
    description: 'Wrapped with fresh toppings and sauces', longDescription: 'Your tikkas wrapped up with fresh toppings and house sauces.', image: 'wrap', glutenFree: false, noun: 'Wrap', allergens: ['Milk', 'Gluten (wheat)'], popular: true, sortOrder: 40,
  },
  just: {
    id: 'just', name: 'Just Tikkas', category: 'just', hasTikkas: true, hasToppings: false, mealAvailable: false, upgradeIds: ['cream'],
    sizes: [
      { name: 'Regular', pricePence: 695, maxPortions: 2, kcal: 330 },
      { name: 'Large', pricePence: 995, maxPortions: 3, kcal: 480 },
      { name: 'Sharer', pricePence: 2995, maxPortions: 5, kcal: 1400 },
    ],
    description: 'No base, no distractions. Boxed.', longDescription: 'Just the tikkas, boxed, with house sauces.', image: 'just', glutenFree: true, noun: 'Box', allergens: MILK, sortOrder: 50,
  },
  chipsS: { id: 'chipsS', name: 'Chips', category: 'sides', hasTikkas: false, hasToppings: false, mealAvailable: false, upgradeIds: [], sizes: [{ name: 'Regular', pricePence: 250, kcal: 310 }, { name: 'Large', pricePence: 450, kcal: 520 }], description: 'Golden chips', sortOrder: 60 },
  totsS: { id: 'totsS', name: 'Tikki Tots', category: 'sides', hasTikkas: false, hasToppings: false, mealAvailable: false, upgradeIds: [], sizes: [{ name: 'Regular', pricePence: 250, kcal: 280 }, { name: 'Large', pricePence: 450, kcal: 470 }], description: 'Crisp spiced potato tots', sortOrder: 70 },
  gulab: { id: 'gulab', name: 'Gulab Jamun', category: 'desserts', hasTikkas: false, hasToppings: false, mealAvailable: false, upgradeIds: [], sizes: [{ name: 'Regular', pricePence: 250, kcal: 300 }], description: 'Warm, in syrup', allergens: MILK, sortOrder: 80 },
  rasg: { id: 'rasg', name: 'Rasgulla', category: 'desserts', hasTikkas: false, hasToppings: false, mealAvailable: false, upgradeIds: [], sizes: [{ name: 'Regular', pricePence: 250, kcal: 190 }], description: 'Soft and chilled', allergens: MILK, sortOrder: 90 },
};

export const SEED_MENU: Menu = {
  items: SEED_ITEMS,
  upgrades: SEED_UPGRADES,
  mealPricePence: 350,
  membersDessertPricePence: 100,
  soldOutFlavours: [],
};

/** Items in menu order, optionally for one category. */
export function menuItems(menu: Menu, category?: Category): MenuItem[] {
  return Object.values(menu.items)
    .filter((it) => !category || it.category === category)
    .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
}

export function isFlavour(v: unknown): v is Flavour {
  return typeof v === 'string' && (FLAVOURS as readonly string[]).includes(v);
}
export function isProtein(v: unknown): v is Protein {
  return typeof v === 'string' && (PROTEINS as readonly string[]).includes(v);
}
export function isSauce(v: unknown): v is Sauce {
  return typeof v === 'string' && (SAUCES as readonly string[]).includes(v);
}
export function isTopping(v: unknown): v is Topping {
  return typeof v === 'string' && (TOPPINGS as readonly string[]).includes(v);
}
